import { useState } from 'react';

import type { ChatMessage, ChatMessageResponse } from '@karehero/models';
import { joinLines } from '@karehero/helpers/string';

import { lastCardID } from 'utils/chatMessages';
import { saidSincePrompts } from 'utils/onboardingV4Messages';
import { usePromptCoverage } from './usePromptCoverage';

export interface UsePinnedPromptsOptions {
  careCircleId?: string;
  /** Keep stable: a new list starts the ticking over. */
  prompts: readonly string[];
  apiMessages: readonly ChatMessageResponse[];
  messages: readonly ChatMessage[];
  /** What is typed or recorded but not yet sent, while they talk. */
  liveText: string;
  /** Whether the prompts are held at the top while they talk. */
  isPinnable: boolean;
  /** Whether what they are saying now ticks the prompts, while they talk or say more. */
  isListening: boolean;
  isEnabled: boolean;
}

/**
 * usePinnedPrompts ticks the opening prompts off as they are spoken to, live
 * words included, and says which card is held until it is put back.
 */
export const usePinnedPrompts = ({
  careCircleId,
  prompts,
  apiMessages,
  messages,
  liveText,
  isPinnable,
  isListening,
  isEnabled,
}: UsePinnedPromptsOptions) => {
  // Pinned until they put it back, so each tick stays in view while they talk.
  const [isKeptPinned, setIsKeptPinned] = useState(true);
  const isPinned = isPinnable && isKeptPinned;
  const togglePinned = () => setIsKeptPinned((wasKept) => !wasKept);
  const cardID = lastCardID(apiMessages, 'prompts');

  const { states, isCaughtUp } = usePromptCoverage({
    careCircleId,
    prompts,
    said: joinLines([saidSincePrompts(messages), isListening ? liveText : '']),
    isEnabled,
  });

  const isAnyLeft = prompts.some((label) => states[label] !== 'done');

  return { states, isCaughtUp, isPinned, isAnyLeft, cardID, togglePinned };
};
